import { useState, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Home, Receipt, CreditCard, Landmark,
  TrendingUp, Menu, ChevronRight, Construction, Settings,
  PanelLeftClose, PanelLeftOpen, Users, LogOut, HardHat, ClipboardList,
  ShieldAlert, DollarSign, KeyRound, Camera, Trash2, ShieldCheck, User, Calendar,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { changePassword, mfaSetup, mfaActivate, mfaDisable, mfaShowQr, uploadProfileImage, deleteProfileImage, updateProfile } from '@/lib/api';
import { UserAvatar } from '@/components/common/UserAvatar';
import { useConfig } from '@/lib/ConfigContext';
import { useAuth } from '@/lib/AuthContext';
import '@/App.css';

const navGroups = [
  {
    label: 'Ejecutivo',
    items: [
      { path: '/', label: 'Dashboard', icon: LayoutDashboard, module: 'dashboard' },
    ],
  },
  {
    label: 'Operación',
    items: [
      { path: '/viviendas', label: 'Viviendas', icon: Home, module: 'viviendas' },
      { path: '/avance-obra', label: 'Avance de Obra', icon: HardHat, module: 'avance_obra' },
      { path: '/estimaciones', label: 'Estimaciones', icon: ClipboardList, module: 'estimaciones' },
    ],
  },
  {
    label: 'Finanzas',
    items: [
      { path: '/egresos', label: 'Egresos', icon: Receipt, module: 'egresos' },
      { path: '/pagos', label: 'Pagos', icon: DollarSign, module: 'pagos' },
      { path: '/credito', label: 'Crédito', icon: CreditCard, module: 'credito' },
      { path: '/prestamos', label: 'Préstamos', icon: Landmark, module: 'prestamos' },
      { path: '/facturacion', label: 'Facturación', icon: Receipt, module: 'pagos' },
    ],
  },
  {
    label: 'Análisis',
    items: [
      { path: '/flujo-caja', label: 'Flujo de Caja', icon: TrendingUp, module: 'flujo_caja' },
      { path: '/validacion', label: 'Validación Operativa', icon: ShieldAlert, module: 'validacion' },
      { path: '/timeline', label: 'Timeline Viviendas', icon: Calendar, module: 'viviendas' },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { path: '/catalogo-obra', label: 'Catálogo de Obra', icon: ClipboardList, module: 'catalogo_obra' },
      { path: '/notificaciones-config', label: 'Notificaciones', icon: ShieldAlert, module: 'configuracion' },
      { path: '/configuracion', label: 'Configuración', icon: Settings, module: 'configuracion' },
      { path: '/usuarios', label: 'Usuarios', icon: Users, module: 'usuarios' },
    ],
  },
];

const pageLabels = {
  '/': 'Dashboard',
  '/viviendas': 'Viviendas',
  '/pagos': 'Pagos',
  '/egresos': 'Egresos',
  '/credito': 'Crédito',
  '/prestamos': 'Préstamos',
  '/flujo-caja': 'Flujo de Caja',
  '/validacion': 'Validación Operativa',
  '/timeline': 'Timeline Viviendas',
  '/avance-obra': 'Avance de Obra',
  '/estimaciones': 'Estimaciones',
  '/avance-semanal': 'Avance Semanal',
  '/catalogo-obra': 'Catálogo de Obra',
  '/notificaciones-config': 'Notificaciones',
  '/configuracion': 'Configuración',
  '/usuarios': 'Usuarios',
};

const SidebarNav = ({ collapsed, onItemClick }) => {
  const { can } = useAuth();

  return (
    <nav className={`flex-1 py-3 space-y-4 overflow-y-auto ${collapsed ? 'px-1.5' : 'px-3'}`} data-testid="sidebar-nav">
      {navGroups.map((group) => {
        const items = group.items.filter(item => can(item.module, 'view'));
        if (items.length === 0) return null;
        return (
          <div key={group.label}>
            {!collapsed && (
              <p className="text-[9px] font-semibold uppercase tracking-[0.15em] text-slate-400/70 mb-1.5 px-3">{group.label}</p>
            )}
            {collapsed && <div className="h-px bg-slate-200/50 mx-2 mb-1.5" />}
            <div className="space-y-0.5">
              {items.map((item) => (
                <Tooltip key={item.path} delayDuration={collapsed ? 100 : 999999}>
                  <TooltipTrigger asChild>
                    <NavLink
                      to={item.path}
                      onClick={onItemClick}
                      data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, '-').replace(/[éá]/g, c => c === 'é' ? 'e' : 'a').replace(/\//g, '')}`}
                      className={({ isActive }) =>
                        `w-full flex items-center gap-3 rounded-lg ${collapsed ? 'justify-center px-2' : 'px-3'} py-2 text-[13px] font-medium transition-all duration-150 ${
                          isActive
                            ? 'bg-[rgba(240,90,40,0.08)] text-slate-950 nav-item-active'
                            : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                        }`
                      }
                      end={item.path === '/'}
                    >
                      <item.icon className="h-[17px] w-[17px] shrink-0" strokeWidth={1.8} />
                      {!collapsed && <span>{item.label}</span>}
                    </NavLink>
                  </TooltipTrigger>
                  {collapsed && <TooltipContent side="right"><p>{item.label}</p></TooltipContent>}
                </Tooltip>
              ))}
            </div>
          </div>
        );
      })}
    </nav>
  );
};

export default function AppLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const location = useLocation();
  const currentLabel = pageLabels[location.pathname] || 'constructOS';
  const { config } = useConfig() || { config: { proyecto_nombre: '' } };
  const projectName = config?.proyecto_nombre || 'constructOS';
  const { user, logout, refreshUser } = useAuth();

  return (
    <div className="flex h-screen bg-[#FAFBFD] app-bg-texture">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex lg:flex-col bg-white border-r border-slate-200/80 shrink-0 transition-all duration-200 ${collapsed ? 'w-[60px]' : 'w-[252px]'}`}
        data-testid="sidebar"
      >
        {/* Brand */}
        <div className={`flex flex-col ${collapsed ? 'py-4 px-2 items-center' : 'py-5 px-5'}`}>
          {collapsed ? (
            <div className="h-8 w-8 rounded-md overflow-hidden shrink-0">
              <img src="/logo-constructos-sm.png" alt="constructOS" className="h-full w-full object-contain" />
            </div>
          ) : (
            <img src="/logo-sidebar.png" alt="constructOS" className="h-7 object-contain object-left" />
          )}
        </div>
        <div className={`h-px bg-slate-100 ${collapsed ? 'mx-2' : 'mx-4'}`} />
        {/* Nav */}
        <SidebarNav collapsed={collapsed} />
        {/* Collapse */}
        <div className={`py-3 mt-auto ${collapsed ? 'px-2' : 'px-3'}`}>
          <div className="h-px bg-slate-100 mb-3" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed(c => !c)}
            className={`h-8 w-full text-slate-400 hover:text-slate-600 ${collapsed ? 'px-0 justify-center' : ''}`}
            data-testid="sidebar-collapse-button"
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <><PanelLeftClose className="h-4 w-4 mr-1" /><span className="text-[10px]">Colapsar</span></>}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-[52px] bg-white/85 backdrop-blur-md border-b border-slate-200/60 flex items-center justify-between px-4 lg:px-6 shrink-0 z-30" data-testid="topbar">
          <div className="flex items-center gap-3">
            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden h-8 w-8" data-testid="mobile-menu-button">
                  <Menu className="h-4.5 w-4.5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[260px] p-0">
                <SheetTitle className="sr-only">Navegación</SheetTitle>
                <div className="px-5 py-5">
                  <img src="/logo-sidebar.png" alt="constructOS" className="h-7 object-contain object-left" />
                </div>
                <SidebarNav onItemClick={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>

            <div className="flex items-center gap-1.5 text-[13px]">
              <span className="text-slate-400 hidden sm:inline">{projectName}</span>
              <ChevronRight className="h-3 w-3 text-slate-300 hidden sm:inline" />
              <span className="font-semibold text-slate-800">{currentLabel}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setProfileOpen(true)}
              className="flex items-center gap-2 rounded-full hover:bg-slate-50 py-1 px-1.5 pr-3 transition-colors"
              data-testid="topbar-avatar"
            >
              <UserAvatar user={user} size="sm" />
              <span className="text-[12px] font-medium text-slate-700 hidden sm:inline">{user?.name || user?.email || ''}</span>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1440px]">
            {children}
          </div>
        </main>
      </div>

      {/* Change Password Dialog */}
      <ChangePasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />
      <MFASetupDialog open={mfaOpen} onClose={() => setMfaOpen(false)} user={user} refreshUser={refreshUser} />
      <UserProfileSheet open={profileOpen} onClose={() => setProfileOpen(false)} user={user} onLogout={logout} onPasswordOpen={() => { setProfileOpen(false); setPwdOpen(true); }} onMfaOpen={() => { setProfileOpen(false); setMfaOpen(true); }} refreshUser={refreshUser} />
    </div>
  );
}

function ChangePasswordDialog({ open, onClose }) {
  const [form, setForm] = useState({ current: '', newPwd: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setError('');
    if (form.newPwd.length < 6) { setError('Mínimo 6 caracteres'); return; }
    if (form.newPwd !== form.confirm) { setError('Las contraseñas no coinciden'); return; }
    setBusy(true);
    try {
      await changePassword({ current_password: form.current, new_password: form.newPwd });
      toast.success('Contraseña actualizada');
      setForm({ current: '', newPwd: '', confirm: '' });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error al cambiar contraseña');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { onClose(); setError(''); setForm({ current: '', newPwd: '', confirm: '' }); } }}>
      <DialogContent className="sm:max-w-sm" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#F05A28]" /> Cambiar Contraseña</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs">Contraseña actual</Label>
            <Input type="password" value={form.current} onChange={e => setForm(f => ({ ...f, current: e.target.value }))} data-testid="pwd-current" />
          </div>
          <div>
            <Label className="text-xs">Nueva contraseña</Label>
            <Input type="password" value={form.newPwd} onChange={e => setForm(f => ({ ...f, newPwd: e.target.value }))} data-testid="pwd-new" />
          </div>
          <div>
            <Label className="text-xs">Confirmar nueva contraseña</Label>
            <Input type="password" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} data-testid="pwd-confirm" />
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={busy || !form.current || !form.newPwd || !form.confirm} data-testid="pwd-save">
            {busy ? 'Guardando...' : 'Cambiar Contraseña'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MFASetupDialog({ open, onClose, user, refreshUser }) {
  const [step, setStep] = useState('idle'); // idle | qr | verify | done | sync_verify | sync_qr
  const [qrData, setQrData] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const mfaEnabled = user?.mfa_enabled;

  const handleSetup = async () => {
    setBusy(true); setError('');
    try {
      const data = await mfaSetup();
      setQrData(data);
      setStep('qr');
    } catch { setError('Error al generar QR'); }
    finally { setBusy(false); }
  };

  const handleActivate = async () => {
    setBusy(true); setError('');
    try {
      await mfaActivate(code);
      toast.success('MFA activado correctamente');
      setStep('done');
      if (refreshUser) refreshUser();
    } catch (err) { setError(err?.response?.data?.detail || 'Código incorrecto'); }
    finally { setBusy(false); }
  };

  const handleDisable = async () => {
    setBusy(true); setError('');
    try {
      await mfaDisable();
      toast.success('MFA desactivado');
      if (refreshUser) refreshUser();
      onClose();
    } catch { setError('Error'); }
    finally { setBusy(false); }
  };

  const handleSyncVerify = async () => {
    setBusy(true); setError('');
    try {
      const data = await mfaShowQr(code);
      setQrData(data);
      setCode('');
      setStep('sync_qr');
    } catch (err) { setError(err?.response?.data?.detail || 'Código incorrecto'); }
    finally { setBusy(false); }
  };

  const handleClose = () => {
    setStep('idle'); setQrData(null); setCode(''); setError('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-sm" data-testid="mfa-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-[#F05A28]" /> Autenticación MFA</DialogTitle>
        </DialogHeader>

        {step === 'idle' && !mfaEnabled && (
          <div className="py-4 text-center space-y-3">
            <p className="text-sm text-slate-600">Protege tu cuenta con una app de autenticación.</p>
            <Button onClick={handleSetup} disabled={busy} data-testid="mfa-start">Activar MFA</Button>
          </div>
        )}

        {step === 'idle' && mfaEnabled && (
          <div className="py-4 text-center space-y-4">
            <div className="h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
              <ShieldCheck className="h-6 w-6 text-emerald-600" />
            </div>
            <p className="text-sm font-medium text-emerald-700">MFA está activo</p>
            <p className="text-xs text-slate-400">Todas las apps que escaneen el mismo QR generarán el mismo código.</p>
            <div className="flex flex-col gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => { setStep('sync_verify'); setCode(''); setError(''); }} data-testid="mfa-sync-btn">
                Sincronizar otra app
              </Button>
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={handleDisable} disabled={busy}>Desactivar MFA</Button>
            </div>
          </div>
        )}

        {step === 'qr' && qrData && (
          <div className="py-2 space-y-4">
            <p className="text-xs text-slate-500 text-center">Escanea este código QR con tu app de autenticación:</p>
            <img src={qrData.qr_code} alt="QR Code" className="mx-auto w-48 h-48 rounded-lg border" />
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-[9px] uppercase tracking-widest text-slate-400 mb-1">Clave manual</p>
              <p className="text-xs font-mono text-slate-700 select-all break-all">{qrData.secret}</p>
            </div>
            <div>
              <Label className="text-xs">Ingresa el código de 6 dígitos para verificar:</Label>
              <Input
                type="text" inputMode="numeric" maxLength={6}
                value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000" className="text-center text-xl tracking-[0.3em] font-mono mt-1"
                data-testid="mfa-verify-code"
              />
            </div>
            {error && <p className="text-xs text-red-500 text-center">{error}</p>}
            <Button className="w-full" onClick={handleActivate} disabled={busy || code.length < 6} data-testid="mfa-activate">
              {busy ? 'Verificando...' : 'Activar MFA'}
            </Button>
          </div>
        )}

        {step === 'sync_verify' && (
          <div className="py-2 space-y-4">
            <p className="text-xs text-slate-500 text-center">Ingresa un código actual para mostrar el QR:</p>
            <Input
              type="text" inputMode="numeric" maxLength={6} autoFocus
              value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000" className="text-center text-xl tracking-[0.3em] font-mono"
              data-testid="mfa-sync-code"
            />
            {error && <p className="text-xs text-red-500 text-center">{error}</p>}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setStep('idle'); setCode(''); setError(''); }}>Cancelar</Button>
              <Button className="flex-1" onClick={handleSyncVerify} disabled={busy || code.length < 6} data-testid="mfa-sync-verify">
                {busy ? 'Verificando...' : 'Mostrar QR'}
              </Button>
            </div>
          </div>
        )}

        {step === 'sync_qr' && qrData && (
          <div className="py-2 space-y-4">
            <p className="text-xs text-slate-500 text-center">Escanea este QR en tu nueva app. Generará el mismo código.</p>
            <img src={qrData.qr_code} alt="QR Code" className="mx-auto w-48 h-48 rounded-lg border" />
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-[9px] uppercase tracking-widest text-slate-400 mb-1">Clave manual</p>
              <p className="text-xs font-mono text-slate-700 select-all break-all">{qrData.secret}</p>
            </div>
            <Button variant="outline" className="w-full" onClick={handleClose}>Cerrar</Button>
          </div>
        )}

        {step === 'done' && (
          <div className="py-6 text-center space-y-3">
            <div className="h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
              <ShieldCheck className="h-6 w-6 text-emerald-600" />
            </div>
            <p className="text-sm font-medium text-emerald-700">MFA activado correctamente</p>
            <p className="text-xs text-slate-400">A partir de ahora se te pedirá el código al iniciar sesión.</p>
            <Button variant="outline" onClick={handleClose}>Cerrar</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* ── User Profile Sheet ── */
function UserProfileSheet({ open, onClose, user, onLogout, onPasswordOpen, onMfaOpen, refreshUser }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const roleLabels = { admin: 'Administrador', editor: 'Editor', visor: 'Visor' };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500000) { toast.error('Imagen demasiado grande. Máximo 500KB.'); return; }
    if (!file.type.startsWith('image/')) { toast.error('Solo se permiten imágenes.'); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await uploadProfileImage(reader.result);
          await refreshUser();
          toast.success('Imagen actualizada');
        } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
        finally { setUploading(false); }
      };
      reader.readAsDataURL(file);
    } catch { setUploading(false); }
  };

  const handleImageDelete = async () => {
    try {
      await deleteProfileImage();
      await refreshUser();
      toast.success('Imagen eliminada');
    } catch { toast.error('Error'); }
  };

  return (
    <Sheet open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <SheetContent className="w-[340px] p-0" data-testid="user-profile-sheet">
        <SheetHeader className="sr-only"><SheetTitle>Mi Cuenta</SheetTitle></SheetHeader>
        <div className="p-6 space-y-6">
          {/* Profile section */}
          <div className="text-center space-y-3">
            <div className="relative inline-block">
              <UserAvatar user={user} size="lg" />
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              <button
                onClick={() => fileRef.current?.click()}
                className="absolute bottom-0 right-0 h-6 w-6 rounded-full bg-[#F05A28] text-white flex items-center justify-center shadow-sm hover:bg-[#d94d20] transition-colors"
                disabled={uploading}
                data-testid="upload-avatar-btn"
              >
                <Camera className="h-3 w-3" />
              </button>
            </div>
            {user?.profile_image && (
              <button onClick={handleImageDelete} className="text-[10px] text-slate-400 hover:text-red-500 block mx-auto">Eliminar foto</button>
            )}
            <div>
              <p className="text-base font-semibold text-slate-900">{user?.name || 'Sin nombre'}</p>
              <p className="text-xs text-slate-400">{user?.email}</p>
              <Badge variant="outline" className="mt-1 text-[9px]">{roleLabels[user?.role] || user?.role}</Badge>
            </div>
          </div>

          <Separator />

          {/* Security section */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-3">Seguridad</p>
            <div className="space-y-2">
              <button onClick={onPasswordOpen} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-slate-50 transition-colors" data-testid="profile-change-pwd">
                <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center"><KeyRound className="h-4 w-4 text-slate-500" /></div>
                <div><p className="text-[13px] font-medium text-slate-700">Cambiar contraseña</p><p className="text-[10px] text-slate-400">Actualiza tu contraseña de acceso</p></div>
              </button>
              <button onClick={onMfaOpen} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-slate-50 transition-colors" data-testid="profile-mfa">
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${user?.mfa_enabled ? 'bg-emerald-50' : 'bg-slate-100'}`}>
                  <ShieldCheck className={`h-4 w-4 ${user?.mfa_enabled ? 'text-emerald-600' : 'text-slate-500'}`} />
                </div>
                <div>
                  <p className="text-[13px] font-medium text-slate-700">Autenticación MFA</p>
                  <p className="text-[10px] text-slate-400">{user?.mfa_enabled ? 'Activo — Google Authenticator' : 'Inactivo — Configura doble autenticación'}</p>
                </div>
              </button>
            </div>
          </div>

          <Separator />

          {/* Session */}
          <button onClick={() => { onClose(); onLogout(); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-red-50 transition-colors group" data-testid="profile-logout">
            <div className="h-8 w-8 rounded-lg bg-slate-100 group-hover:bg-red-100 flex items-center justify-center"><LogOut className="h-4 w-4 text-slate-500 group-hover:text-red-500" /></div>
            <div><p className="text-[13px] font-medium text-slate-700 group-hover:text-red-600">Cerrar sesión</p></div>
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

